import { BugAntIcon } from '@heroicons/react/24/outline'
import { useRef } from 'react'
import styled from 'styled-components'

import Loading from '@/components/Card/Loading'
import { RemoveCircle } from '@/components/Icons'
import InlineErrorMessage from '@/components/InlineErrorMessage/InlineErrorMessage'
import Modal from '@/components/Modal'
import Button from '@/components/TailwindUI/Button'
import { StyledDivider } from '@/pages/SpaceAppBuilder/components/StyledComponents'

const Icon = styled.div`
  width: 40px;
  height: 40px;

  align-items: center;
  justify-content: center;

  display: flex;
  background-color: #f6f6f6;
  border-radius: 3px;
  padding: 0.5rem;

  cursor: pointer;
`
const RightIcon = styled(Icon)`
  margin-left: auto;

  g {
    circle {
      fill: none;
    }

    path {
      stroke: black;
    }
  }
`

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(243, 243, 244, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`

interface DebugModalProps {
  isOpen?: boolean
  onDebugLoggingChange: (b?: boolean) => void
  onClose: () => void
  isLoading: boolean
  isDebugLoggingEnabled: boolean
  isError: boolean
}

const DebugModal = ({
  isOpen = true,
  onDebugLoggingChange,
  onClose,
  isLoading,
  isDebugLoggingEnabled,
  isError
}: DebugModalProps) => {
  const initialFocus = useRef(null)
  const onChange = (reset?: boolean) => {
    onDebugLoggingChange(reset)
    if (!isError && !isLoading) {
      onClose()
    }
  }
  return (
    <Modal className="max-w-lg" isOpen={isOpen} onClose={onClose} size="xs" initialFocus={initialFocus}>
      {isLoading && (
        <LoaderOverlay>
          <Loading isLoading={true} loadingMessage={'Updating debug mode...'} />
        </LoaderOverlay>
      )}
      <div className="mx-5 flex items-center">
        <BugAntIcon className="h-6 w-6 text-blue-700" />
        <div className="ml-2 font-bold">{'Debug Mode'}</div>
        <RightIcon onClick={onClose}>
          <RemoveCircle />
        </RightIcon>
      </div>
      <div className="m-5">
        <div className="mb-10">
          <div className="font-bold">{'Logs are captured for all errors and failures by default.'}</div>
          <p className="text-base">{'Debug mode will capture logs for all events for 1 day'}</p>
        </div>

        {isError && <InlineErrorMessage subMessage={'Failed to update feature. Try again later'} />}
        <StyledDivider />
      </div>
      {isDebugLoggingEnabled ? (
        <div className="mx-6 my-2 flex justify-between">
          <Button color="transparent" onClick={onClose} ref={initialFocus}>
            <span className="font-xs font-normal text-blue-700">{'Cancel'}</span>
          </Button>
          <div className="flex justify-end gap-2">
            <Button color="secondary" onClick={() => onChange(true)}>
              <span className="font-xs font-normal">{'Reset timer'}</span>
            </Button>
            <Button color="warning" onClick={() => onChange()}>
              <span className="font-xs font-normal">{'Turn off debug mode'}</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="mx-6 my-2 flex justify-end gap-2">
          <Button color="transparent" onClick={onClose} ref={initialFocus}>
            <span className="font-xs font-normal text-blue-700">{'Cancel'}</span>
          </Button>
          <Button color="primary" onClick={() => onChange()}>
            <span className="font-xs font-normal">{'Debug for 1 day'}</span>
          </Button>
        </div>
      )}
    </Modal>
  )
}

export default DebugModal
